import React from 'react'
import PropTypes from 'prop-types'

import { FOOTER_HEIGHT, HEADER_THEME, SPACING } from '../components/constants.js'
import Footer from '../components/Footer.jsx'
import Grid from '../components/Grid.jsx'
import Header from '../components/Header.jsx'
import Margin from '../components/Margin.jsx'
import Notes from '../components/Notes.jsx'
import Slide from '../components/Slide.jsx'
import LoadingState from '../components/LoadingState.jsx'
import ZeroState from '../components/ZeroState.jsx'

import useRect from '../hooks/useRect.js'

import MultiLineChartHOC from '../charts/MultiLineChartHOC.jsx'

export const LABEL_MAP = {
  None: null, // DEPRECATED: None as label
  Percent: 'Percent of Engagement (%)',
  Count: 'Total',
}

const TIME_FRAME = {
  day_of_year: 'DAY',
  week_of_year: 'WEEK',
  month_of_year: 'MONTH',
}

const LABEL_TITLE = {
  None: 'ENGAGEMENT', // DEPRECATED: None as label
  Percent: 'PERCENT OF EVENTS',
  Count: 'TOTAL EVENTS',
}

const Multiline = (props) => {
  const [HeaderRect, headerRect] = useRect()
  const [HeaderChartRect, headerChartRect] = useRect()
  const [NotesRect, notesRect] = useRect()

  const {
    title,
    description,
    timeFrame,
    data,
    notes,
    page,
    isLoading,
    yLabel,
    replaceLegend,
  } = props

  const chartTitle = `${LABEL_TITLE[yLabel]} BY ${TIME_FRAME[timeFrame]}`

  return (
    <Slide>
      <Margin left={SPACING} top={SPACING} right={SPACING} bottom={SPACING + FOOTER_HEIGHT}>
        <Grid
          direction="vertical"
          gap={48}
          items={[
            {
              component: (
                <Header
                  title={title}
                  description={description}
                  {...HEADER_THEME.VERTICAL}
                  DelegatedRect={HeaderRect}
                />
              ),
              size: headerRect,
            },
            {
              component: (
                <>
                  <Header
                    title={chartTitle}
                    {...HEADER_THEME.MULTILINE}
                    DelegatedRect={HeaderChartRect}
                  />
                  {isLoading ? (
                    <LoadingState marginTop={headerChartRect.height} />
                  ) : (
                    <ZeroState condition={!!data?.length} marginTop={headerChartRect.height}>
                      <MultiLineChartHOC
                        data={data}
                        yLabel={LABEL_MAP[yLabel]}
                        headerChartRect={headerChartRect}
                        replaceLegend={replaceLegend}
                      />
                    </ZeroState>
                  )}
                </>
              ),
              size: 1,
            },
            ...(notes
              ? [
                  {
                    component: <Notes content={notes} align="left" DelegatedRect={NotesRect} />,
                    size: notesRect,
                  },
                ]
              : []),
          ]}
        />
      </Margin>
      <Footer page={page} theme={{ background: 'dark', icon: 'light', page: 'light' }} />
    </Slide>
  )
}

Multiline.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  chartTitle: PropTypes.string,
  chartDescription: PropTypes.string,
  notes: PropTypes.string,
  page: PropTypes.number,
  chart: PropTypes.func,
  isLoading: PropTypes.bool,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      month: PropTypes.string.isRequired,
      values: PropTypes.shape({}), // keys are used as label, value is a number and a data point
    })
  ),
  yLabel: PropTypes.oneOf(Object.keys(LABEL_TITLE)).isRequired,
  timeFrame: PropTypes.oneOf(Object.keys(TIME_FRAME)).isRequired,
  replaceLegend: PropTypes.objectOf(PropTypes.string),
}

Multiline.defaultProps = {
  title: '',
  description: '',
  chartTitle: '',
  chartDescription: '',
  notes: '',
  page: 0,
  chart: () => {},
  isLoading: false,
  data: null,
  yLabel: null,
}

export default Multiline
